/**
 * Polls for a confirmed Supabase session after a user clicks an email
 * confirmation link. Confirmation typically happens out-of-band (a different
 * tab/device), so there is no push channel back to this tab — polling is
 * the only option. The interval is self-terminating: it clears itself
 * synchronously inside the poll callback the instant a terminal outcome
 * (confirmed or error) is found, rather than relying on a follow-up
 * re-render to tear down the effect.
 */
import { supabase } from "@/services/supabaseClient";
import type { AuthError, Session } from "@supabase/supabase-js";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { AUTH_PATHS } from "../../../constants";
import { authOperations } from "../../../store/authMachine";

type PollOutcome =
  | { type: "pending" }
  | { type: "confirmed"; session: Session }
  | { type: "error" };

export const resolvePollOutcome = (response: {
  data: { session: Session | null };
  error: AuthError | null;
}): PollOutcome => {
  const {
    data: { session },
    error,
  } = response;
  if (error) {
    return { type: "error" };
  }
  if (session) {
    return { type: "confirmed", session };
  }
  return { type: "pending" };
};

export const useEmailConfirmationPoll = () => {
  const [isConfirmed, setIsConfirmed] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;
    // eslint-disable-next-line prefer-const -- assigned below so the closure can clear it
    let intervalId: ReturnType<typeof setInterval>;

    const pollEmailConfirmation = async () => {
      const response = await supabase.auth.getSession();
      if (cancelled) {
        return;
      }

      const outcome = resolvePollOutcome(response);
      switch (outcome.type) {
        case "error":
          clearInterval(intervalId);
          navigate(AUTH_PATHS.SIGNIN, { replace: true });
          break;
        case "confirmed":
          clearInterval(intervalId);
          authOperations.notifyEmailConfirmed(outcome.session);
          setIsConfirmed(true);
          break;
        case "pending":
          break;
      }
    };

    pollEmailConfirmation();
    intervalId = setInterval(pollEmailConfirmation, 2000);
    return () => {
      cancelled = true;
      clearInterval(intervalId);
    };
  }, [navigate]);

  return isConfirmed;
};
